import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Box, ButtonDropdown, SpaceBetween, Button } from '@amzn/awsui-components-react';
import { MultidimensionalAllocationContext } from 'src/services/multidimensional-allocation/MultidimensionalAllocationContext';
import { ACTIONS } from 'src/services/multidimensional-allocation/MultidimensionalAllocationReducer';
import StringUtils from 'src/utils/stringUtils';
import * as Moment from 'moment';
import { extendMoment } from 'moment-range';

/**
 * Props for the WorksheetPastExecutionResultsComponent
 */
interface WorksheetPastExecutionResultsComponentProps {
    worksheetId?: string;
    worksheetName?: string;
}

/**
 * Component that allows users to view past execution results for a multidimensional allocation worksheet.
 * Provides a dropdown to select from the last 12 months of execution periods and displays historical data.
 * 
 * @param props - The component props
 * @returns JSX element containing the past execution results interface
 */
export function WorksheetPastExecutionResultsComponent(props: WorksheetPastExecutionResultsComponentProps) {
    const { worksheetId, worksheetName } = props;
    const { services, multidimensionalAllocationState, multidimensionalAllocationDispatch } = 
        useContext(MultidimensionalAllocationContext);

    const { executionPeriodWasTouched } = multidimensionalAllocationState;

    const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
    
    // API call to get worksheet execution for selected period
    const [worksheetExecutionResult, worksheetExecutionLoading, worksheetExecutionError] = 
        services.multidimensionalAllocationService.getMultiAllocationWorksheetExecution(worksheetId, selectedPeriod || undefined);

    // Generate execution periods (last 12 months)
    const getExecutionPeriods = () => {
        const executionPeriods: any[] = [];
        executionPeriods.push({ id: 'Clear Selection', text: 'Clear Selection' });
        const moment = extendMoment(Moment);
        const endDate = moment().subtract(1, 'months').date(1);
        Array.from(moment.rangeFromInterval('month', -11, endDate).by('month')).reverse().forEach(d => {
            const formattedDate = d.format('MMM-YYYY').toUpperCase();
            executionPeriods.push({ id: formattedDate, text: formattedDate });
        });
        return executionPeriods;
    };

    const executionPeriods = useMemo(getExecutionPeriods, []);

    // Handle worksheet execution result
    useEffect(() => {
        if (worksheetExecutionResult == null || selectedPeriod == null) {
            return;
        }
        
        if (StringUtils.isNullOrEmpty(worksheetExecutionResult.executionId)) {
            services.messageService.showErrorBanner(
                `Cannot find worksheet execution for ${selectedPeriod}.`
            );
            setSelectedPeriod(null);
        } else {
            multidimensionalAllocationDispatch(
                ACTIONS.SET_EXECUTION_PERIOD.withPayload(selectedPeriod)
            );
            multidimensionalAllocationDispatch(
                ACTIONS.SET_WORKSHEET_EXECUTION.withPayload(worksheetExecutionResult)
            );
            // Trigger data sources refresh to load historical data
            multidimensionalAllocationDispatch(ACTIONS.SET_DATA_SOURCES_NEED_REFRESH.withPayload(true));
        }
    }, [worksheetExecutionResult, selectedPeriod, services.messageService, multidimensionalAllocationDispatch]);

    // Handle clearing selection
    useEffect(() => {
        if (executionPeriodWasTouched && selectedPeriod == null) {
            // Reset view to current data
            multidimensionalAllocationDispatch(
                ACTIONS.SET_EXECUTION_PERIOD.withPayload(null)
            );
            multidimensionalAllocationDispatch(
                ACTIONS.SET_WORKSHEET_EXECUTION.withPayload(null)
            );
            // Trigger data sources refresh to load current data
            multidimensionalAllocationDispatch(ACTIONS.SET_DATA_SOURCES_NEED_REFRESH.withPayload(true));
        }
    }, [selectedPeriod, executionPeriodWasTouched, multidimensionalAllocationDispatch]);

    return (
        <SpaceBetween size="s" direction="horizontal">
            {selectedPeriod && (
                <Button
                    variant="normal"
                    onClick={() => {
                        setSelectedPeriod(null);
                        multidimensionalAllocationDispatch(
                            ACTIONS.SET_EXECUTION_PERIOD_TOUCHED.withPayload(true)
                        );
                    }}
                >
                    Return to Current View
                </Button>
            )}
            <ButtonDropdown
                className="polarisSmallButtonDropdown"
                disabled={worksheetExecutionLoading}
                items={executionPeriods}
                onItemClick={({ detail }) => {
                    multidimensionalAllocationDispatch(
                        ACTIONS.SET_EXECUTION_PERIOD_TOUCHED.withPayload(true)
                    );
                    setSelectedPeriod(
                        detail.id === 'Clear Selection'
                            ? null
                            : detail.id
                    );
                }}
            >
                {worksheetExecutionLoading
                    ? `Checking ${selectedPeriod} executions...`
                    : (selectedPeriod == null
                        ? 'View past worksheet execution results'
                        : selectedPeriod)
                }
            </ButtonDropdown>
        </SpaceBetween>
    );
}
